import { useState } from 'react';
import { useI18n } from '@/i18n';
import type { GameState } from '@/game/types';
import { shareGameLink } from '@/shared/share';
import { cn } from '@/shared/cn';
import type { MessageKey } from '@/i18n/pt';

interface ShareButtonProps {
  state: GameState;
  labelKey: MessageKey;
  textKey: MessageKey;
  className?: string;
}

export function ShareButton({ state, labelKey, textKey, className }: ShareButtonProps) {
  const { t } = useI18n();
  const [feedback, setFeedback] = useState<string | null>(null);

  async function handleShare() {
    setFeedback(null);
    try {
      const result = await shareGameLink({
        state,
        title: t('brand'),
        text: t(textKey),
      });
      if (result === 'copied') {
        setFeedback(t('copyLink'));
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      setFeedback(t('shareFailed'));
    }
  }

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => void handleShare()}
        className={cn(
          'w-full rounded-2xl bg-vz-accent px-4 py-3 text-center font-display text-base font-bold text-vz-accent-ink shadow-lg shadow-teal-900/10 transition hover:brightness-110 active:scale-[0.98]',
          className,
        )}
      >
        {t(labelKey)}
      </button>
      {feedback ? <p className="text-center text-sm font-semibold text-vz-muted">{feedback}</p> : null}
    </div>
  );
}
